/*
 * Zoovu Orca "Product Finder" GA4 event bridge (EMKT-775).
 *
 * Forwards every ZoovuSDK.events.listenToAll(...) interaction to
 * gtag('event', ...) with send_to set to this market's GA4 measurement ID.
 * Ported from the JP/IN implementations (see architect/design_note.md for
 * the full rationale behind the sanitizer's key/name caps).
 *
 * Relies on the global `CustomElement` base class defined in core.js
 * (loaded synchronously in <head> via app-essentials.liquid).
 */
class ZoovuGaTracking extends CustomElement {
  props = {
    sendTo: '',
  }

  mounted() {
    super.mounted()

    if (typeof window.ZoovuSDK?.assistantInit !== 'function') return

    window.ZoovuSDK.assistantInit(() => this.onAssistantInit())
  }

  onAssistantInit() {
    if (typeof window.ZoovuSDK?.events?.listenToAll !== 'function') return
    if (window.__zoovuGaListenerAttached) return

    window.__zoovuGaListenerAttached = true

    window.ZoovuSDK.events.listenToAll((event) => this.onZoovuEvent(event))

    console.log('[zoovu-ga-tracking] listener attached')
  }

  onZoovuEvent(event) {
    try {
      if (typeof window.gtag !== 'function') return

      const data = this.flattenProductFields(event?.name, event?.data)
      const { name, sanitizedData } = this.resolveEventNameAndData(event?.name, data)

      window.gtag('event', name, { ...sanitizedData, send_to: this.props.sendTo })
    } catch (e) {
      console.warn('[zoovu-ga-tracking] failed to forward event', e)
    }
  }

  // Only these two event types carry product data GA4 can't accept as-is
  // (a nested object / an array of objects); everything else passes through
  // the generic sanitizer untouched.
  flattenProductFields(eventName, data) {
    if (!data || typeof data !== 'object') return data

    if (eventName === 'go-to-product-details' && data.product && typeof data.product === 'object') {
      const { product, ...rest } = data
      return { product_sku: product.sku, product_name: product.name, ...rest }
    }

    if (eventName === 'recommendations-fetch' && Array.isArray(data.products)) {
      const { products, ...rest } = data
      const [first] = products

      return { product_sku: first?.sku, product_name: first?.name, ...rest }
    }

    return data
  }

  // event.name gets a stricter check than data keys: GA4 is known to drop the
  // entire hit (not just the bad param) on an invalid event name.
  resolveEventNameAndData(rawName, data) {
    if (this.isValidEventName(rawName)) {
      return { name: rawName, sanitizedData: this.sanitize(data, 24) }
    }

    console.warn('[zoovu-ga-tracking] invalid event name', rawName)

    // Cap reduced to 23 here so the appended orig_event_name (24th key) never
    // pushes sanitizedData past the 24-key budget shared with the valid path.
    const sanitizedData = this.sanitize(data, 23)
    sanitizedData.orig_event_name = this.truncate(String(rawName ?? ''), 100)

    return { name: this.fallbackEventName(rawName), sanitizedData }
  }

  isValidEventName(name) {
    return typeof name === 'string' && name.length <= 40 && /^[A-Za-z][A-Za-z0-9_]*$/.test(name)
  }

  fallbackEventName(rawName) {
    const cleaned = String(rawName ?? '')
      .replace(/^[^A-Za-z]+/, '')
      .replace(/[^A-Za-z0-9_]/g, '_')
      .slice(0, 40)

    return cleaned || 'zoovu_event_unknown'
  }

  sanitize(data, cap) {
    const result = {}

    if (!data || typeof data !== 'object') return result

    Object.keys(data)
      .slice(0, cap)
      .forEach((key) => {
        const sanitizedValue = this.sanitizeValue(data[key])

        if (sanitizedValue === undefined) return

        result[this.truncate(key, 40)] = sanitizedValue
      })

    return result
  }

  sanitizeValue(value) {
    if (value === null || value === undefined) return undefined

    if (typeof value === 'string') return this.truncate(value, 100)

    if (typeof value === 'number' || typeof value === 'boolean') return value

    if (Array.isArray(value)) {
      const hasObjectItem = value.some((item) => item !== null && typeof item === 'object')

      return hasObjectItem ? undefined : this.truncate(value.join(','), 100)
    }

    // Plain nested object: GA4 params must be scalar, dropped rather than stringified.
    return undefined
  }

  truncate(value, maxLength) {
    return value.length > maxLength ? value.slice(0, maxLength) : value
  }
}

if (!customElements.get('zoovu-ga-tracking')) {
  customElements.define('zoovu-ga-tracking', ZoovuGaTracking)
}
